import { MouseButton } from '@y42/platform';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { withoutUndoManager } from './../../utils/without-undo-manager';
import { FREE_HAND_DRAWING_TOOL_NAME } from './config';
import { simplifySvgPath } from './simplify-svg-path';

export class FreeHandDrawingTool extends go.Tool {
  constructor() {
    super();
    this.name = FREE_HAND_DRAWING_TOOL_NAME;
    this.isEnabled = false;
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  private readonly defaultShapeProperties: go.ObjectData = {
    strokeWidth: 1.5,
  };
  private temporaryShape: go.Shape | null = null;
  private overlay: HTMLDivElement | null = null;
  private oldDefaultCursor?: {
    diagram: string;
    document: string;
  };
  shapeProperties: go.ObjectData = this.defaultShapeProperties;
  allowedClickClass = '';
  cursor = '';

  insertAction: ((position: go.Point, geoString: string) => void) | null = null;

  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.doCancel();
    }
  }

  startManually(insertAction: (position: go.Point, geoString: string) => void) {
    this.diagram.currentTool = this;
    this.isEnabled = true;
    this.insertAction = insertAction;
    this.doActivate();
  }

  canStart() {
    if (!this.isEnabled) {
      return false;
    }
    const diagram = this.diagram;
    if (diagram === null || diagram.isReadOnly || diagram.isModelReadOnly) {
      return false;
    }
    if (!diagram.allowInsert) {
      return false;
    }

    return true;
  }

  doActivate() {
    super.doActivate();
    this.diagram.isMouseCaptured = true;
    this.oldDefaultCursor = {
      diagram: this.diagram.defaultCursor,
      document: document.body.style.cursor,
    };
    this.diagram.defaultCursor = this.cursor;
    document.body.style.cursor = this.cursor;
    this.addOverlay();

    document.addEventListener('keydown', this.onKeyDown);
  }

  doDeactivate() {
    super.doDeactivate();
    withoutUndoManager(this.diagram, () => {
      if (isNotNullish(this.temporaryShape) && this.temporaryShape.part) {
        this.diagram.remove(this.temporaryShape.part);
      }
      if (this.oldDefaultCursor) {
        document.body.style.cursor = this.oldDefaultCursor.document;
        this.diagram.defaultCursor = this.oldDefaultCursor.diagram;
      }
      this.diagram.isMouseCaptured = false;
      this.isActive = false;
      this.isEnabled = false;

      this.shapeProperties = this.defaultShapeProperties;
      this.insertAction = null;
      this.removeOverlay();

      document.removeEventListener('keydown', this.onKeyDown);
    });
  }

  addPoint({ x: pointX, y: pointY }: go.Point) {
    const shape = this.temporaryShape;
    if (isNullish(shape)) {
      return;
    }

    const { x: viewportX, y: viewportY } = this.diagram.viewportBounds.position;
    const q = new go.Point(pointX - viewportX, pointY - viewportY);

    const part = shape.part;
    if (isNullish(part?.diagram)) {
      const figure = new go.PathFigure(q.x, q.y, true);
      const geometry = new go.Geometry().add(figure);
      const { strokeWidth } = shape;
      if (this.temporaryShape) {
        this.temporaryShape.geometry = geometry;
      }
      if (part) {
        part.position = new go.Point(viewportX - strokeWidth / 2, viewportY - strokeWidth / 2);
        this.diagram.add(part);
      }
    }

    const segments = shape.geometry?.figures.first()?.segments || new go.List();
    const idx = segments.count - 1;
    if (idx >= 0) {
      const last = segments.elt(idx);
      if (Math.abs(q.x - last.endX) < 0.5 && Math.abs(q.y - last.endY) < 0.5) {
        return;
      }
    }

    const geometry = shape.geometry?.copy();
    const figure = geometry?.figures.first();
    const segment = new go.PathSegment(go.PathSegment.Line, q.x, q.y);
    figure?.add(segment);
    shape.geometry = geometry || null;
  }

  doMouseDown() {
    if (isNullish(this.diagram) || !this.isAllowedClick() || !this.isActive) {
      return;
    }
    this.createTemporaryPart();
  }

  doMouseMove() {
    if (isNullish(this.diagram) || !this.isActive || isNullish(this.temporaryShape)) {
      return;
    }
    this.addPoint(this.diagram.lastInput.documentPoint);
  }

  doMouseUp() {
    if (this.isActive && this.temporaryShape?.geometry) {
      const { x: viewportX, y: viewportY } = this.diagram.viewportBounds.position;
      const geo = this.temporaryShape.geometry.copy();
      const position = geo.normalize();
      position.x = viewportX - position.x;
      position.y = viewportY - position.y;

      const geometryPoints =
        this.temporaryShape?.geometry?.figures
          ?.first()
          ?.segments.map((x) => [x.endX, x.endY] as [number, number])
          .toArray() || [];
      const simplifiedPath = simplifySvgPath(geometryPoints);

      if (this.insertAction) {
        this.insertAction(new go.Point(position.x, position.y), simplifiedPath);
      }
    }
    if (this.temporaryShape?.part) {
      this.diagram.remove(this.temporaryShape.part);
    }
    this.temporaryShape = null;
    this.stopTool();
  }

  private createTemporaryPart() {
    const $ = go.GraphObject.make;

    this.temporaryShape = $(go.Shape, {
      name: 'SHAPE',
      fill: null,
      strokeCap: 'round',
      strokeJoin: 'round',
      ...this.shapeProperties,
    });
    $(go.Part, { layerName: 'Tool' }, this.temporaryShape);
  }

  private isAllowedClick() {
    const { div, lastInput } = this.diagram;
    const clickedWithPrimaryBtn =
      lastInput.event instanceof MouseEvent && lastInput.event.button === MouseButton.Primary;
    const clickedOnCanvas = lastInput?.event?.target instanceof Node && div?.contains(lastInput.event.target);
    const clickedOnAllowedClass =
      isNotNullish(this.allowedClickClass) &&
      lastInput?.event?.target instanceof Element &&
      lastInput.event.target.classList.contains(this.allowedClickClass);

    return clickedWithPrimaryBtn && (clickedOnCanvas || clickedOnAllowedClass);
  }

  private addOverlay() {
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.style.width = '100%';
      this.overlay.style.height = '100%';
      this.overlay.style.zIndex = '99999';
      this.overlay.style.position = 'fixed';
      this.overlay.style.top = '0';
      this.overlay.style.left = '0';
    }

    this.overlay.addEventListener('click', this.doDeactivate);
    this.diagram.div?.appendChild(this.overlay);
  }

  private removeOverlay() {
    this.overlay?.removeEventListener('click', this.doDeactivate);
    if (this.overlay?.parentElement === this.diagram.div) {
      this.diagram.div?.removeChild(this.overlay);
    } else if (this.overlay?.remove) {
      this.overlay.remove();
    }
  }
}
